import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How Much Support Does a Missionary Need? A Realistic Monthly Budget Breakdown';
const DESCRIPTION =
    "A practical framework for building your monthly missionary support budget — the categories most first-time budgets miss, how sending organizations set their numbers, and how to land on a figure you can defend to supporters.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover17.jpg',
            datePublished: '2026-07-21',
            dateModified: '2026-07-21',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-much-support-does-a-missionary-need',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How much monthly support does a missionary typically need?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'There is no universal number — it depends on family size, cost of living in the host country, and whether your sending organization bundles in benefits, insurance, and field-level overhead. The only reliable way to get your number is to build a real budget with your sending organization rather than copy a figure from another missionary.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What categories do first-time support budgets usually miss?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Health insurance and medical evacuation coverage, sending-organization administrative fees, retirement contributions, periodic furlough/home-assignment travel, language school or cultural training, and a buffer for currency fluctuation are the categories new missionaries most often underestimate or leave out entirely.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I raise more than my minimum budget number?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Most experienced missionaries and sending organizations recommend raising 10-20% above your calculated minimum. Support attrition is normal — some monthly partners stop giving every year due to job loss, moving, or life changes — and a buffer keeps a normal amount of attrition from turning into a funding crisis.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Does my sending organization set my support goal for me?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Usually yes, or at least it reviews and approves your number. Sending organizations typically have a standard budget worksheet that accounts for field costs, home-office overhead, and benefits specific to your country and family size. Always start there before building your own estimate from scratch.",
                    },
                },
            ],
        },
    ],
};

export default function HowMuchSupportDoesAMissionaryNeed() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-much-support-does-a-missionary-need' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-support-budget-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover17.jpg" alt="A missionary family reviewing a budget together at their kitchen table" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>"How much should I ask for?" is usually the first question a new missionary asks, and it's tempting to answer it by copying whatever number a friend on the field raised. That's a mistake — your number depends on your specific field, family, and sending organization, not on someone else's. Here's how to actually build it.</p>

                    <h2>Start with your sending organization's worksheet, not a blank spreadsheet</h2>

                    <p>Almost every established sending organization has a standard support-budget worksheet built for the country or region you're headed to. It already accounts for things you'd otherwise guess at: local cost of living, currency risk, home-office overhead, and organization-specific benefits. Ask for it before you build anything yourself. If your organization is small or new and doesn't have one, use the categories below as your starting checklist and have a financial advisor or a veteran missionary sanity-check the total.</p>

                    <h2>The categories a real budget needs</h2>

                    <p><strong>Field living costs.</strong> Housing, food, local transportation, and utilities at your destination — not your home country's cost of living. A missionary moving from the US Midwest to a major Latin American or Southeast Asian city may find rent higher than expected; someone headed to a rural post may find it lower. Get real numbers from missionaries already on that field rather than estimating from general cost-of-living indexes.</p>

                    <p><strong>Health insurance and medical evacuation coverage.</strong> This is the single most commonly underestimated line item. International health coverage plus medevac insurance can run several hundred dollars a month per family and is not optional — most sending organizations require it before you're cleared to go.</p>

                    <p><strong>Sending-organization administrative fee.</strong> Most organizations take a percentage of raised support (commonly in the 8-15% range) to cover home-office staff, member care, accounting, and compliance. This is a real cost of the infrastructure that makes tax-deductible giving and field support possible — build it into your target number rather than treating it as a surprise deduction later.</p>

                    <p><strong>Retirement and benefits.</strong> Missionaries who don't work a conventional salaried job for years or decades still need retirement contributions. Many organizations bundle a modest retirement line into the standard budget; if yours doesn't, add one yourself.</p>

                    <p><strong>Furlough / home-assignment travel.</strong> Most long-term missionaries return home periodically to reconnect with supporters and family. Flights for a family of four or five, every one to four years depending on your organization's policy, add up — budget for it as a recurring line, not a one-time surprise.</p>

                    <p><strong>Language school and cultural training.</strong> If your field requires language acquisition, factor in tuition and materials, especially in the first year or two.</p>

                    <p><strong>A currency and inflation buffer.</strong> If you're raising support in one currency and spending in another, exchange-rate swings can quietly erode your budget over a multi-year term. A 5-10% buffer absorbs normal fluctuation without forcing an emergency re-ask.</p>

                    <h2>Why the number you raise should be higher than your minimum</h2>

                    <p>Support attrition is normal, not a sign you did something wrong. Every year, a portion of monthly partners stop giving — they change jobs, move, have a baby, or simply forget to update a card that expired. Most experienced missionaries build in a 10-20% cushion above their calculated minimum specifically to absorb this normal churn without triggering a funding crisis eighteen months in. If your minimum is $4,000/month, a realistic raising target is closer to $4,400-$4,800/month.</p>

                    <h2>Turning the number into an ask</h2>

                    <p>Once you have a defensible total, break it into a concrete unit people can say yes to — "40 households at $50/month" or "20 partners at $100/month" reads as achievable, where "we need $4,500/month" feels abstract. This is the same principle behind <a href="/resources/how-to-write-a-missionary-support-letter">a clear, specific support letter</a>: a number people can act on beats a number they have to interpret.</p>

                    <p>It also matters that supporters can see the recurring commitment they're making and how it adds up over time. A monthly-giving page that tracks progress against your specific target — rather than a one-time crowdfunding total — mirrors the actual shape of your budget: predictable, recurring, and reviewable at a glance.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How much monthly support does a missionary typically need?</strong><br />
                    There's no universal number. It depends on family size, field cost of living, and your organization's benefits and fees. Build a real budget with your sending organization rather than copying another missionary's total.</p>

                    <p><strong>What categories do first-time support budgets usually miss?</strong><br />
                    Health insurance and medevac coverage, administrative fees, retirement contributions, furlough travel, language training, and a currency buffer are the most commonly underestimated or omitted line items.</p>

                    <p><strong>Should I raise more than my minimum budget number?</strong><br />
                    Yes — most experienced missionaries recommend a 10-20% cushion above the minimum to absorb normal support attrition without a funding crisis.</p>

                    <p><strong>Does my sending organization set my support goal for me?</strong><br />
                    Usually it provides or approves the worksheet. Start there before building your own estimate from scratch.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/how-long-to-raise-full-support-as-a-missionary">How Long Does It Take to Raise Full Support as a Missionary?</a></li>
                        <li><a href="/calculator">Use the support calculator</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built our platform around recurring monthly support, so a lot of what's above comes from talking with missionaries about how they actually built their numbers — and what they wish they'd budgeted for from day one.</p>
                    <p><small><em>Last updated: 2026-07-21.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-much-support-does-a-missionary-need" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
